/**
 * Tabbed media picker — Emoji / Stickers / GIFs.
 *
 * Privacy model (SPEC §1.2, §19, NFR-PWA-002):
 * - Emoji + sticker packs render from bundled local data; usage stays on-device.
 * - Online GIF search is OFF until the user enables it. It runs through the
 *   same-origin proxy (/api/gif/*) and the chosen GIF is re-sent through the
 *   E2EE attachment path — no direct provider contact, no IP leak.
 * - Local GIF/sticker import is always available and needs no consent.
 * - My stickers (made with the on-device sticker maker, or saved from a chat)
 *   are sealed at rest under the vault key (stickers/myStickers.ts).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { IconEdit, IconImage, IconPlus, IconSearch, IconTrash } from "@veto/ui";
import { EMOJI_GROUPS, searchEmojis } from "./emojiData";
import {
  STICKER_PACKS,
  getBuiltinSticker,
  stickerWireBody,
  type BuiltinSticker,
} from "./stickerArt";
import {
  loadEmojiRecents,
  loadGifRecents,
  loadStickerRecents,
  rememberEmoji,
  rememberGif,
  rememberSticker,
  type GifRecent,
} from "./mediaRecents";
import { fetchGifFile, gifFileName, searchGifs, type GifResult } from "../gifClient";
import { loadGifPrefs, saveGifPrefs } from "../gifPrefs";
import { useLongPress } from "../hooks/useLongPress";
import { showAppToast } from "../appToast";
import {
  addMySticker,
  loadMyStickers,
  myStickerFile,
  onMyStickersChanged,
  removeMySticker,
  type MySticker,
} from "../stickers/myStickers";
import { openStickerMaker } from "./StickerMaker";
import { chooseInLayer, confirmInLayer } from "./StickerLayer";

export type MediaTab = "emoji" | "stickers" | "gifs";

export function EmojiPicker({
  onPick,
  onClose,
  initialTab = "emoji",
  onSendSticker,
  onSendStickerFile,
  onSendGifFile,
}: {
  onPick: (emoji: string) => void;
  onClose: () => void;
  initialTab?: MediaTab;
  onSendSticker?: (packId: string, stickerId: string) => void;
  onSendStickerFile?: (file: File) => void;
  onSendGifFile?: (file: File) => void;
}) {
  const [tab, setTab] = useState<MediaTab>(initialTab);
  useEffect(() => setTab(initialTab), [initialTab]);

  return (
    <div className="emoji-popover media-picker" role="dialog" aria-label="Emoji picker">
      <div className="emoji-popover-head">
        <div className="media-tabs" role="tablist" aria-label="Media picker tabs">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "emoji"}
            className={tab === "emoji" ? "active" : ""}
            onClick={() => setTab("emoji")}
          >
            Emoji
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "stickers"}
            className={tab === "stickers" ? "active" : ""}
            onClick={() => setTab("stickers")}
          >
            Stickers
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "gifs"}
            className={tab === "gifs" ? "active" : ""}
            onClick={() => setTab("gifs")}
          >
            GIFs
          </button>
        </div>
        <button type="button" className="btn ghost small" onClick={onClose}>
          Close
        </button>
      </div>
      {tab === "emoji" && <EmojiTab onPick={onPick} />}
      {tab === "stickers" && (
        <StickersTab
          onSendBuiltin={onSendSticker ?? (() => {})}
          onSendFile={onSendStickerFile ?? (() => {})}
        />
      )}
      {tab === "gifs" && <GifsTab onSendFile={onSendGifFile ?? (() => {})} />}
    </div>
  );
}

/* ------------------------------- Emoji tab ------------------------------- */

function EmojiTab({ onPick }: { onPick: (emoji: string) => void }) {
  const [query, setQuery] = useState("");
  const [groupId, setGroupId] = useState(EMOJI_GROUPS[0]!.id);
  const [recents, setRecents] = useState<string[]>(() => loadEmojiRecents());

  const group = useMemo(
    () => EMOJI_GROUPS.find((g) => g.id === groupId) ?? EMOJI_GROUPS[0]!,
    [groupId],
  );
  const searching = query.trim().length > 0;
  const results = useMemo(() => (searching ? searchEmojis(query) : []), [query, searching]);

  const pick = (e: string) => {
    setRecents(rememberEmoji(e));
    onPick(e);
  };

  return (
    <>
      <div className="emoji-search">
        <IconSearch size={16} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search emoji (local)"
          aria-label="Search emojis"
        />
      </div>
      {recents.length > 0 && !searching && (
        <>
          <div className="emoji-group-label">Recent</div>
          <div className="emoji-grid">
            {recents.map((e) => (
              <button key={`r-${e}`} type="button" className="emoji-cell" onClick={() => pick(e)}>
                {e}
              </button>
            ))}
          </div>
        </>
      )}
      {searching ? (
        results.length > 0 ? (
          <div className="emoji-grid" style={{ marginTop: 8 }}>
            {results.map((e) => (
              <button key={e} type="button" className="emoji-cell" onClick={() => pick(e)}>
                {e}
              </button>
            ))}
          </div>
        ) : (
          <p className="muted picker-empty" role="status">
            Nothing matches “{query.trim()}”.
          </p>
        )
      ) : (
        <>
          <div className="emoji-cat-tabs" role="tablist" aria-label="Emoji categories">
            {EMOJI_GROUPS.map((g) => (
              <button
                key={g.id}
                type="button"
                role="tab"
                title={g.label}
                aria-label={g.label}
                aria-selected={g.id === group.id}
                className={g.id === group.id ? "active" : ""}
                onClick={() => setGroupId(g.id)}
              >
                {g.icon}
              </button>
            ))}
          </div>
          <div className="emoji-group-label">{group.label}</div>
          <div className="emoji-grid">
            {group.emojis.map((e) => (
              <button
                key={e}
                type="button"
                className="emoji-cell"
                title={group.keywords.get(e)}
                onClick={() => pick(e)}
              >
                {e}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* ----------------------------- Stickers tab ------------------------------ */

const MINE_TAB = "mine";
const RECENT_TAB = "recent";

/** Blob URLs for My stickers (revoked when the list changes or the tab closes). */
function useStickerUrls(list: MySticker[]): Map<string, string> {
  const [urls, setUrls] = useState<Map<string, string>>(() => new Map());
  useEffect(() => {
    if (typeof URL.createObjectURL !== "function") return;
    const next = new Map(
      list.map((s) => [s.id, URL.createObjectURL(new Blob([s.bytes as BlobPart], { type: s.mediaType }))]),
    );
    setUrls(next);
    return () => {
      for (const u of next.values()) URL.revokeObjectURL(u);
    };
  }, [list]);
  return urls;
}

function searchStickers(query: string): { packId: string; sticker: BuiltinSticker }[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return STICKER_PACKS.flatMap((p) =>
    p.stickers
      .filter((s) => s.keywords.toLowerCase().includes(q) || s.id.includes(q))
      .map((sticker) => ({ packId: p.id, sticker })),
  );
}

function StickersTab({
  onSendBuiltin,
  onSendFile,
}: {
  onSendBuiltin: (packId: string, stickerId: string) => void;
  onSendFile: (file: File) => void;
}) {
  const [query, setQuery] = useState("");
  /** null = automatic: My stickers when there are some, else Recent, else the first pack. */
  const [chosenTab, setChosenTab] = useState<string | null>(null);
  const [recents, setRecents] = useState<string[]>(() => loadStickerRecents());
  const [mine, setMine] = useState<MySticker[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const alive = useRef(true);
  const optionsOpen = useRef(false);
  const lp = useLongPress();
  const urls = useStickerUrls(mine);

  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    const refresh = () => {
      void loadMyStickers()
        .then((list) => {
          if (!cancelled) setMine(list);
        })
        .catch(() => {
          /* locked / storage unavailable: My stickers stays empty */
        });
    };
    refresh();
    const off = onMyStickersChanged(refresh);
    return () => {
      cancelled = true;
      alive.current = false;
      off();
    };
  }, []);

  const recentStickers = recents
    .map((ref) => {
      const [packId, id] = ref.split("/");
      const sticker = packId && id ? getBuiltinSticker(packId, id) : undefined;
      return sticker && packId ? { packId, sticker } : null;
    })
    .filter((x): x is { packId: string; sticker: BuiltinSticker } => x !== null);

  const tab =
    chosenTab ?? (mine.length > 0 ? MINE_TAB : recentStickers.length > 0 ? RECENT_TAB : STICKER_PACKS[0]!.id);
  const pack = STICKER_PACKS.find((p) => p.id === tab);
  const searching = query.trim().length > 0;
  const results = useMemo(() => searchStickers(query), [query]);

  /** Errors show in the picker while it is open, otherwise as an app toast. */
  const report = (message: string) => {
    if (alive.current) setError(message);
    else showAppToast(message);
  };

  const sendBuiltin = (packId: string, id: string) => {
    // Keep the grid in place: a new Recent must not flip the automatic tab.
    if (chosenTab === null) setChosenTab(tab);
    setRecents(rememberSticker(`${packId}/${id}`));
    onSendBuiltin(packId, id);
  };

  const create = async (file: File) => {
    setError(null);
    if (file.type && (!file.type.startsWith("image/") || /svg/i.test(file.type))) {
      setError("Choose a photo (JPEG, PNG, WebP…).");
      return;
    }
    const sticker = await openStickerMaker(file);
    if (!sticker) return;
    let toSend: File;
    try {
      const saved = await addMySticker(sticker);
      toSend = myStickerFile(saved);
    } catch (e) {
      report(e instanceof Error ? e.message : "Couldn't save the sticker.");
      const type = sticker.type === "image/webp" ? "image/webp" : "image/png";
      toSend = new File([sticker], `sticker-${Date.now()}.${type.slice(6)}`, { type });
    }
    if (!alive.current) {
      // The chat changed or the picker closed meanwhile: keep it, don't send it somewhere else.
      showAppToast("Saved to My stickers");
      return;
    }
    setChosenTab(MINE_TAB);
    onSendFile(toSend); // same E2EE path as any custom sticker (CT 9, kind "sticker")
  };

  const edit = async (s: MySticker) => {
    const sticker = await openStickerMaker(new Blob([s.bytes as BlobPart], { type: s.mediaType }), {
      editing: true,
    });
    if (!sticker) return;
    try {
      await addMySticker(sticker, { replaceId: s.id });
    } catch (e) {
      report(e instanceof Error ? e.message : "Couldn't save the sticker.");
    }
  };

  const openOptions = async (s: MySticker) => {
    // Android fires both our long-press and a native contextmenu: open once.
    if (optionsOpen.current) return;
    optionsOpen.current = true;
    try {
      const choice = await chooseInLayer("My sticker", [
        { id: "edit", label: "Edit sticker", icon: <IconEdit size={18} /> },
        { id: "delete", label: "Delete", icon: <IconTrash size={18} />, danger: true },
      ]);
      if (choice === "edit") {
        await edit(s);
      } else if (choice === "delete") {
        const ok = await confirmInLayer({
          title: "Delete this sticker?",
          body: "It is removed from My stickers on this device.",
          action: "Delete",
          danger: true,
        });
        if (ok) await removeMySticker(s.id);
      }
    } finally {
      optionsOpen.current = false;
    }
  };

  const tabButton = (id: string, label: string) => (
    <button
      key={id}
      type="button"
      role="tab"
      aria-selected={tab === id}
      className={tab === id ? "active" : ""}
      onClick={() => {
        setChosenTab(id);
        setQuery("");
      }}
    >
      {label}
    </button>
  );

  const builtinCell = (packId: string, s: BuiltinSticker) => (
    <button
      key={`${packId}/${s.id}`}
      type="button"
      className="sticker-cell"
      onClick={() => sendBuiltin(packId, s.id)}
      title={s.keywords}
      aria-label={`Send sticker ${s.id}${s.animated ? " (animated)" : ""}`}
    >
      <img src={s.uri} alt="" loading="lazy" />
      {s.animated && <span className="sticker-animated-dot" aria-label="Animated" />}
    </button>
  );

  return (
    <>
      <div className="sticker-search-row">
        <div className="emoji-search">
          <IconSearch size={16} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search stickers"
            aria-label="Search stickers"
            enterKeyHint="search"
          />
        </div>
        <button
          type="button"
          className="sticker-import sticker-create-btn"
          onClick={() => fileRef.current?.click()}
          data-testid="sticker-create"
        >
          <IconPlus size={14} /> Create
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) void create(f);
          }}
        />
      </div>
      {error && (
        <p className="picker-error" role="alert">
          {error}
        </p>
      )}
      {searching ? (
        results.length > 0 ? (
          <div className="sticker-grid">{results.map((r) => builtinCell(r.packId, r.sticker))}</div>
        ) : (
          <p className="muted picker-empty" role="status">
            Nothing matches “{query.trim()}”.
          </p>
        )
      ) : (
        <>
          <div className="sticker-pack-tabs" role="tablist" aria-label="Sticker packs">
            {tabButton(MINE_TAB, "My stickers")}
            {recentStickers.length > 0 && tabButton(RECENT_TAB, "Recent")}
            {STICKER_PACKS.map((p) => tabButton(p.id, p.label))}
          </div>
          {tab === MINE_TAB ? (
            <>
              <div className="emoji-group-label">My stickers</div>
              <div className="sticker-grid">
                <button
                  type="button"
                  className="sticker-cell sticker-create-cell"
                  onClick={() => fileRef.current?.click()}
                  aria-label="Create a sticker from a photo"
                >
                  <IconImage size={22} />
                  <span>Create</span>
                </button>
                {mine.map((s) => (
                  <div key={s.id} className="sticker-cell-wrap">
                    <button
                      type="button"
                      className="sticker-cell sticker-mine-cell"
                      aria-label="Send my sticker. Long-press or right-click to edit or delete."
                      onPointerDown={(e) => lp.begin(e, () => void openOptions(s))}
                      onPointerMove={lp.move}
                      onPointerUp={lp.end}
                      onPointerCancel={lp.end}
                      onPointerLeave={lp.end}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        lp.cancel();
                        void openOptions(s);
                      }}
                      onClick={() => {
                        if (lp.consumeFired()) return; // the long-press already opened the menu
                        onSendFile(myStickerFile(s));
                      }}
                    >
                      {urls.get(s.id) && <img src={urls.get(s.id)} alt="" draggable={false} />}
                    </button>
                    <button
                      type="button"
                      className="sticker-remove sticker-more"
                      aria-label="Sticker options"
                      title="Edit or delete"
                      onClick={() => void openOptions(s)}
                    >
                      ⋯
                    </button>
                  </div>
                ))}
              </div>
              {mine.length === 0 && (
                <p className="muted picker-empty">
                  Make a sticker from any photo — it stays encrypted on this device.
                </p>
              )}
            </>
          ) : tab === RECENT_TAB ? (
            <>
              <div className="emoji-group-label">Recent</div>
              <div className="sticker-grid">{recentStickers.map((r) => builtinCell(r.packId, r.sticker))}</div>
            </>
          ) : pack ? (
            <>
              <div className="emoji-group-label">{pack.label}</div>
              <div className="sticker-grid">{pack.stickers.map((s) => builtinCell(pack.id, s))}</div>
            </>
          ) : null}
        </>
      )}
    </>
  );
}

/* ------------------------------- GIFs tab -------------------------------- */

function GifsTab({ onSendFile }: { onSendFile: (file: File) => void }) {
  const [prefs, setPrefs] = useState(() => loadGifPrefs());
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GifResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState<string | null>(null);
  const [recents, setRecents] = useState<GifRecent[]>(() => loadGifRecents());
  const fileRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<number | null>(null);

  const runSearch = async (q: string) => {
    setLoading(true);
    setError(null);
    try {
      const kind = q.trim() ? "gifs" : "featured";
      const r = await searchGifs(q, kind);
      setResults(r);
      if (r.length === 0 && q.trim()) setError(`Nothing for “${q.trim()}” — try another term.`);
    } catch (e) {
      setResults(null);
      setError(e instanceof Error ? e.message : "Search failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!prefs.onlineSearchEnabled) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => void runSearch(query), 350);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query, prefs.onlineSearchEnabled]);

  const enable = () => {
    const next = { onlineSearchEnabled: true };
    setPrefs(next);
    saveGifPrefs(next);
  };

  const disable = () => {
    const next = { onlineSearchEnabled: false };
    setPrefs(next);
    saveGifPrefs(next);
    setResults(null);
  };

  const sendGif = async (result: GifResult) => {
    setSending(result.id);
    try {
      const file = await fetchGifFile(result.full, gifFileName(result));
      setRecents(rememberGif({ full: result.full, preview: result.preview, desc: result.desc }));
      onSendFile(file);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not fetch GIF");
    } finally {
      setSending(null);
    }
  };

  if (!prefs.onlineSearchEnabled) {
    return (
      <div className="gif-consent">
        <strong>GIF search (optional)</strong>
        <p className="muted text-sm">
          Search GIFs online via Tenor through Veto’s own proxy: your IP and the API key are
          never shared with the provider — but the words you search are sent to Tenor. GIFs you
          send are always re-encrypted end to end like any attachment.
        </p>
        <p className="muted text-sm">
          Prefer full privacy? Send GIFs and stickers <em>from your device</em> below — that stays
          completely local.
        </p>
        <div className="gif-consent-actions">
          <button type="button" className="btn small" onClick={enable}>
            Enable GIF search
          </button>
          <button type="button" className="btn ghost small" onClick={() => fileRef.current?.click()}>
            From device
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/gif"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onSendFile(f);
              e.target.value = "";
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="emoji-search">
        <IconSearch size={16} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search Tenor GIFs…"
          aria-label="Search GIFs"
          enterKeyHint="search"
        />
        <button type="button" className="btn ghost small" onClick={disable} title="Disable online GIF search">
          Off
        </button>
      </div>
      <div className="gif-actions">
        <button type="button" className="btn ghost small" onClick={() => fileRef.current?.click()}>
          From device
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/gif"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onSendFile(f);
            e.target.value = "";
          }}
        />
      </div>
      {error && (
        <p className="picker-error" role="alert">
          {error}
        </p>
      )}
      {loading && (
        <p className="muted picker-empty" role="status">
          Searching…
        </p>
      )}
      {recents.length > 0 && (
        <>
          <div className="emoji-group-label">Recent</div>
          <div className="gif-grid">
            {recents.map((g) => (
              <button
                key={g.full}
                type="button"
                className="gif-cell"
                onClick={() => void fetchGifFile(g.full, "recent.gif").then(onSendFile).catch(() => setError("Could not fetch GIF"))}
                title={g.desc}
              >
                <img src={g.preview} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        </>
      )}
      {results && results.length > 0 && (
        <div className="gif-grid">
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              className="gif-cell"
              onClick={() => void sendGif(r)}
              disabled={sending === r.id}
              title={r.desc || "Send GIF"}
              aria-label={`Send GIF ${r.desc || r.id}`}
            >
              <img src={r.preview} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** Convenience helper used by chat actions for built-in send. */
export function builtinStickerWire(packId: string, stickerId: string): string {
  return stickerWireBody(packId, stickerId);
}

export type { BuiltinSticker };
